<template>
  <block-action
    class="hero__wrapper p-relative d-block"
    :block-action="blockAction"
    :style="{ padding: blockPadding }"
  >
    <template v-slot="{ isLocked, isLoadingAccess, availableAfter }">
      <div
        :class="[
          { '--playing': playing, '--percent': isPercentHeight },
          'hero p-relative'
        ]"
        :style="[
          {
            '--hero-height': imageHeight,
            '--hero-height-scaled': scaledHeight(imageHeight),
            borderRadius: blockBorderRadius,
            boxShadow: styles.boxShadow
          }
        ]"
      >
        <div
          class="p-absolute hero__background"
          :style="{
            backgroundImage: hasImage ? `url(${getImageUrl(image)})` : null,
            backgroundSize: styles.size,
            backgroundPosition: styles.position,
            opacity: imageOpacity
          }"
        ></div>
        <div
          :class="[
            {
              '--shadow': overlayShadow,
              '--audio': hasAudio
            },
            `--y-${styles.textPositionY}`,
            `--x-${styles.textPositionX}`,
            'hero__overlay d-flex h-100 w-100',
            'hero__shadow'
          ]"
        >
          <video-embed
            v-if="hasVideo"
            :video="video"
            :thumbnail="getImageUrl(image)"
            :thumbnail-opacity="hasImage ? imageOpacity : 1"
            :autoplay="videoOptions.autoplay || false"
            :hide-controls="videoOptions.hideControls || false"
            :auto-initialize="videoOptions.autoInitialize || false"
            :plays-inline="videoOptions.playsInline"
            :portrait="videoOptions.portrait || false"
            :muted="videoOptions.muted || false"
            :loop="videoOptions.loop || false"
            :cover="videoOptions.cover || false"
            :finished-action="videoFinishedAction"
            @ready="e => $emit('videoReady', e)"
            @play="() => (playing = true)"
            @pause="() => (playing = false)"
            @stop="() => (playing = false)"
          />

          <audio-embed
            v-else-if="hasAudio"
            :audio="audio"
            :thumbnail="getImageUrl(image)"
          />

          <transition name="fade">
            <slot
              v-if="!playing"
              :height="
                isPercentHeight ? imageHeight : scaledHeight(imageHeight)
              "
            />
          </transition>
        </div>

        <template v-if="isLocked || isLoadingAccess">
          <access-lock
            overlay
            :is-loading="isLoadingAccess"
            :available-after="availableAfter"
          />
        </template>
      </div>
    </template>
  </block-action>
</template>

<script>
import { isPresent, getImageUrl } from "@/lib/utils"
import {
  withRatio,
  toPadding,
  ratioToMultiplier,
  scaledHeight,
  BLOCK_DEFAULT_HEIGHT_RATIO,
  HERO_HEIGHT_RATIO_RANGE
} from "@/lib/stream-utils"

import BlockAction from "@/components/BlockAction"

const BLOCK_BASE_HEIGHT = 200

const BORDER_RADIUS_BASE = 12
const TEXT_POSITIONS = {
  START: "start",
  CENTER: "center",
  END: "end"
}
const HEIGHT_UNITS = {
  PIXEL: "pixel",
  PERCENT: "percent"
}
const DEFAULT_STYLE = {
  size: "cover", // cover, auto
  position: "center center", // center center, top left, top right, bottom left, bottom right, top center, bottom center
  padding: "0rem",
  boxShadow: null,
  heightRatio: BLOCK_DEFAULT_HEIGHT_RATIO,
  heightRatioUnit: "pixel",
  baseHeight: BLOCK_BASE_HEIGHT,
  borderRadiusRatio: 0,
  textPositionX: TEXT_POSITIONS.START,
  textPositionY: TEXT_POSITIONS.END
}

export default {
  components: {
    BlockAction,
    AccessLock: () => import("@/components/AccessLock"),
    VideoEmbed: () => import("@/components/VideoEmbed"),
    AudioEmbed: () => import("@/components/AudioEmbed")
  },

  props: {
    image: { type: Object, default: () => ({}) },
    styleOptions: {
      type: Object,
      default: () => DEFAULT_STYLE
    }, // center, top_left, top_right, bottom_left,
    video: { type: Object, default: null },
    audio: { type: Object, default: null },
    videoOptions: { type: Object, default: () => ({}) },
    // { autoplay: Boolean, hideControls: Boolean, playsInline: Boolean }
    overlayShadow: { type: Boolean, default: true },
    spaceXRatio: {
      type: Number,
      default: 0
    },
    spaceTopRatio: {
      type: Number,
      default: 0
    },
    spaceBottomRatio: {
      type: Number,
      default: 0
    },
    imageOpacityRatio: {
      type: Number,
      default: 100
    },
    blockAction: { type: Object, default: null },
    videoFinishedAction: { type: Object, default: null }
  },

  data() {
    return {
      playing: false
    }
  },

  computed: {
    hasImage() {
      return isPresent(this.image)
    },

    hasVideo() {
      return isPresent(this.video)
    },

    hasAudio() {
      return isPresent(this.audio)
    },

    hasContent() {
      return this.hasImage || this.hasVideo
    },

    shouldShowVideo() {
      return (!this.hasImage && this.hasVideo) || this.playing
    },

    styles() {
      const result = { ...DEFAULT_STYLE }

      for (const style in this.styleOptions) {
        const value = this.styleOptions[style]
        if (value || value === 0) {
          result[style] = value
        }
      }

      return result
    },

    isPercentHeight() {
      return this.styles.heightRatioUnit === HEIGHT_UNITS.PERCENT
    },

    imageHeight() {
      if (this.isPercentHeight) {
        return `calc(var(--app-height-initial) * ${ratioToMultiplier(
          this.styles.heightRatio,
          HERO_HEIGHT_RATIO_RANGE.MIN,
          HERO_HEIGHT_RATIO_RANGE.MAX
        )})`
      } else {
        return withRatio(this.styles.baseHeight, this.styles.heightRatio, {
          unit: "px"
        })
      }
    },

    blockBorderRadius() {
      return withRatio(BORDER_RADIUS_BASE, this.styles.borderRadiusRatio, {
        integer: true
      })
    },

    blockPadding() {
      return toPadding(
        this.spaceTopRatio,
        this.spaceXRatio,
        this.spaceBottomRatio
      )
    },

    imageOpacity() {
      return withRatio(1, this.imageOpacityRatio, { unit: null })
    }
  },

  methods: {
    getImageUrl,
    withRatio,
    scaledHeight
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

.hero {
  @include container;

  width: 100%;
  overflow: hidden;
  min-height: 200px; // Fallback
  min-height: var(--hero-height);
  display: flex;
  flex-direction: column;

  &:not(.--percent) {
    min-height: var(--hero-height-scaled);
  }

  .blocks-container {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: $video--controls-z-index - 1;

    .block {
      width: 100%;
    }

    .v-button,
    .v-link {
      position: relative;
      z-index: 5;
    }
  }
}

.hero__overlay,
.hero__background {
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
}

.hero__background {
  transition: transform 0.1s linear;
  background-repeat: no-repeat;
}

.hero__overlay {
  flex-grow: 1;

  &.--shadow {
    position: relative;
    background: linear-gradient(
      to bottom,
      rgba($color-fixed-rgb--black, 0) 60%,
      rgba($color-fixed-rgb--black, 0.32)
    );
  }

  &.--audio {
    .blocks-container {
      padding-bottom: 116px; // audio player height
    }
  }

  &.--x-start {
    .blocks-container {
      text-align: left;
      align-items: flex-start;
    }
  }

  &.--x-center {
    .blocks-container {
      text-align: center;
      align-items: center;
    }
  }

  &.--x-end {
    .blocks-container {
      text-align: right;
      align-items: flex-end;
    }
  }

  &.--y-start {
    .blocks-container {
      & > .block:last-child {
        margin-bottom: auto;
      }
    }
  }

  &.--y-center {
    .blocks-container {
      & > .block:first-child {
        margin-top: auto;
      }

      & > .block:last-child {
        margin-bottom: auto;
      }
    }
  }

  &.--y-end {
    .blocks-container {
      & > .block:first-child {
        margin-top: auto;
      }
    }
  }
}

.hero__spacer {
  &.--no-content {
    height: $size--10;
    background: white;
  }
}

.app-page
  > [data-transparent-toolbar] // Only apply to transparent toolbars
  + .blocks-container // Sibling blocks container
  > .block[data-type="hero_block"]:first-child // Only first hero block
 {
  .hero {
    &:not(.--percent) {
      min-height: calc(var(--hero-height) + env(safe-area-inset-top));
      min-height: calc(var(--hero-height-scaled) + env(safe-area-inset-top));

      .blocks-container {
        margin-top: env(safe-area-inset-top);
      }
    }
  }
}
</style>
